import { createSlice } from "@reduxjs/toolkit";
import { getUserData } from "./api.ts";
import { BusinessModels, CompanyTiers } from "../constants.ts";
import { logger } from "../../../utils/consoleLogger.ts";

type UserDetailType = {
  company_id: number;
  company_user_id: number;
  company_name?: string;
  company_logo?: string | null;
  days_remaining?: number;
  first_name?: string;
  email: string;
  is_admin: boolean;
  is_edit_admin: boolean;
  slug: string;
  has_active_credits: boolean;
  has_unused_credits: boolean;
  has_subcompany?: boolean;
  enable_ai_talent_discovery?: boolean;
  business_model: (typeof BusinessModels)[keyof typeof BusinessModels];
  // 1 = legacy billing, 2 = seat-based company plans (Starter/Team).
  billing_version?: number;
  company_tier?: (typeof CompanyTiers)[keyof typeof CompanyTiers];
  // "HR" | "CHRO" | "Hiring Manager" | "None" — the member's level label.
  company_user_role?: string;
  // Job fair (marketplace) company: many employers' recruiters in one slug.
  // Every fair-only UI branch keys on this; normal companies never set it.
  is_marketplace?: boolean;
  participating_employer?: { id: number; name: string } | null;
  marketplace_reason_pills?: string[];
  // AI Candidate Search tab is on for this company (APPLICANT_SEARCH_COMPANIES).
  applicant_search_enabled?: boolean;
  plg_info: {
    version: AUTH_VERSION;
    days_remaining?: number;
    token_refresh_time?: string;
    tokens_used_today?: number;
    max_daily_tokens?: number;
    roles_created?: number;
    max_roles?: number;
    roles_remaining?: number;
  };
};

export enum AUTH_VERSION {
  FREE = "free",
  PAID = "paid",
}

type UserStateType = {
  user: UserDetailType;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | object | null;
  // Slug the latest getUserData call was made for ("" = no slug in URL).
  // Lets App.tsx tell "data for this URL has loaded" apart from stale data
  // belonging to a previously visited company.
  requestedSlug?: string;
};

const loggedOut: UserStateType = {
  isAuthenticated: false,
  user: {
    company_id: -1,
    company_user_id: -1,
    email: "",
    is_admin: false,
    plg_info: null,
    slug: "",
    is_edit_admin: false,
  },
  loading: false,
  error: null,
};

const initialState: UserStateType = {
  isAuthenticated: false,
  user: {
    company_id: -1,
    company_user_id: -1,
    first_name: "",
    email: "",
    is_admin: false,
    slug: "",
    plg_info: {
      version: AUTH_VERSION.FREE,
      days_remaining: 0,
      token_refresh_time: null,
      tokens_used_today: 0,
      max_daily_tokens: 0,
      roles_created: 0,
      max_roles: 0,
      roles_remaining: 0,
    },
  },
  loading: true,
  error: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state) => {
      logger.info("User logging out");
      window.localStorage.clear();
      state = loggedOut;
      window.location.reload();
    },
    setLoaderForUser: (state, action) => {
      state.loading = action.payload;
    },
    setUserData: (state, action) => {
      let temp = action.payload;
      if (temp?.plg_info == null)
        temp.plg_info = {
          version: AUTH_VERSION.PAID,
        };
      state.user = temp;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserData.pending, (state, action) => {
        state.loading = true;
        state.requestedSlug = action.meta.arg;
      })
      .addCase(getUserData.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.loading = false;
      })
      .addCase(getUserData.rejected, (state, action) => {
        logger.error("getUserData rejected", { error: action.error.message, status: action.payload?.status });
        state.error = action.error.message || "Login failed";
        if ([401, 403, 404].includes(action.payload.status)) {
          // A user mid-onboarding has no company yet, so this fetch failing
          // is expected — wiping the session here would log them out
          // between OTP and the onboarding form.
          let needsOnboarding = false;
          try {
            needsOnboarding = !!JSON.parse(
              window.localStorage.getItem("babblebots_auth_data") || "null"
            )?.needsOnboarding;
          } catch {
            /* unreadable auth data: fall through to the clear */
          }
          if (!needsOnboarding) window.localStorage.clear();
        }
        state.loading = false;
      });
    // .addCase(getPLGInfo.pending, (state) => {
    //   // state.loading = true;
    // })
    // .addCase(getPLGInfo.fulfilled, (state, action) => {
    //   state.user.plg_info = action.payload;
    //   // state.loading = false;
    // })
    // .addCase(getPLGInfo.rejected, (state, action) => {
    //   state.error =
    //     action.error.message || "Unable to fetch access information";
    //   state.loading = false;
    // });
  },
});

export const { logout, setUserData, setLoaderForUser } = userSlice.actions;
export default userSlice.reducer;
